import React, { PropsWithChildren, useState, useEffect, useCallback } from 'react';
import { usePrevious } from '@uidotdev/usehooks';
import clsx from 'clsx';

export type TabViewProps = {
  index: number;
	// Dismount tabs that aren't active
	dismount?: boolean;
	speed?: number; // seconds
	className?: string;
}

export function TabView({ index = 0, children, dismount, speed = 0.3, className }: PropsWithChildren<TabViewProps>) {
	const [visibleChildren, setVisibleChildren] = useState<number[]>([index]);
	const prevIndex = usePrevious(index);

	useEffect(() => {
		if (prevIndex !== index) {
			setVisibleChildren([prevIndex, index]);
			const timer = setTimeout(() => {
				setVisibleChildren([index]);
			}, speed * 1000);
			return () => clearTimeout(timer);
		}
		return () => {};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [index]);

	const getAnimationStyle = useCallback((i: number) => {
		const isEntering = i === index;
		if (index < prevIndex) {
			return `${isEntering ? 'enter' : 'exit'}-right ${speed}s cubic-bezier(0.2,0,0,1) forwards`;
		} else if (index > prevIndex) {
			return `${isEntering ? 'enter' : 'exit'}-left ${speed}s cubic-bezier(0.2,0,0,1) forwards`;
		} else {
			return 'none';
		}
	}, [index, prevIndex, speed]);

	return (
		<div className={clsx('relative', className)}>
			{React.Children.map(children, (child, i) => {
				const isActive = i === index;
				const isVisible = visibleChildren.includes(i) || isActive;
				if (dismount && !isVisible) {
					return null;
				}
				return (
					<div
						key={i}
						className={clsx(
							'h-full',
							isActive ? 'relative z-1' : 'absolute inset-0 z-0',
							!isVisible && 'hidden',
						)}
						style={{
							animation: getAnimationStyle(i),
						}}
					>
						{child}
					</div>
				);
			})}
		</div>
	);
}
